<template>
  <m-sider
    :class="['main-navigation', $store.state.breakpoint.smAndDown ? '-mobile' : '']"
    :width="width"
    :bordered="bordered"
    @mouseover="hover = true"
    @mouseleave="hover = false"
  >
    <div class="_content">
      <div
        :class="['_top', showTopBorder ? '-line' : '']"
      >
        <div class="_header">
          <account-menu
            :class="['_account-menu', { '-hug': canUseAIChat }]"
            :show-close="hover && !canUseAIChat"
            @toggle-sidebar="$emit('toggle-sidebar')"
          />
          <div
            v-if="canUseAIChat"
            class="_header-actions"
          >
            <inbox
              icon-only
              :class="{ '_disabled-when-expired': isExpired }"
            />
            <m-btn
              v-if="!breakpoint.smAndDown"
              small
              :icon="desktopSidebarHidden ? 'double-right' : 'double-left'"
              hide-border
              fab
              light
              @click="$emit('toggle-sidebar')"
            />
          </div>
        </div>
        <div :class="{ '_disabled-when-expired': isExpired }">
          <search-box :field="canUseAIChat" />
          <div
            v-if="canUseAIChat"
            class="_tabs"
          >
            <sidebar-icon-tab
              v-for="item in tabNavigation"
              :id="`${item.id}-tab`"
              :key="item.id"
              :title="item.title"
              :icon="item.icon"
              labeled
              :active="item.id === sidebarTab"
              @click="sidebarTab = item.id"
            />
          </div>
          <template v-else>
            <nav-items
              :items="topNavigation1"
              hover-color="beige"
            />
            <inbox />
          </template>
        </div>
      </div>
      <div
        ref="middleRef"
        class="_middle"
        @scroll="handleMiddleScroll"
      >
        <ai-chat-nav-section
          v-if="showsAiChatTab"
          :class="{ '_disabled-when-expired': isExpired }"
        />
        <div
          v-else
          :class="{ '_disabled-when-expired': isExpired }"
        >
          <nav-items
            v-if="canUseAIChat"
            :items="topNavigation1"
            hover-color="beige"
          />
          <div
            v-if="favorites.length > 0"
            :class="['_group', favoritesExpanded ? '-expanded' : '']"
          >
            <nav-sub-heading
              :title="$t('mainNavigation.favorites')"
              clickable
              hover-color="beige"
              @click="toggleSection('favorites')"
            />
            <favorites-nav-items
              v-if="favoritesExpanded"
              :favorites="favorites"
            />
          </div>
          <div :class="['_group', spacesExpanded ? '-expanded' : '']">
            <nav-sub-heading
              :title="$t('mainNavigation.myWorkspace')"
              class="_my-workspace"
              clickable
              hover-color="beige"
              @click="toggleSection('spaces')"
            />
            <my-workspace
              v-if="spacesExpanded"
              id="spaces"
              :expand-svc="expandSvc"
            />
          </div>
          <div
            v-if="tools.length > 0"
            :class="['_group', toolsExpanded ? '-expanded' : '']"
          >
            <nav-sub-heading
              :title="$t('mainNavigation.tools')"
              clickable
              hover-color="beige"
              @click="toggleSection('tools')"
            />
            <div
              v-if="toolsExpanded"
              class="_tools-content -expanded"
            >
              <collapsable-nav-items
                v-for="item in tools"
                :key="item.title"
                :item="item"
                :indentation-level="0"
                :expand-svc="expandSvc"
                hover-color="beige"
              />
            </div>
          </div>
        </div>
        <div
          v-if="!showsAiChatTab"
          class="_secondary-items"
        >
          <nav-items
            :items="bottomNavigation"
            hover-color="beige"
          />
          <div :class="{ '_disabled-when-expired': isExpired }">
            <trash />
          </div>
        </div>
      </div>
      <div
        :class="['_bottom', showBottomBorder ? '-line' : '']"
      >
        <changelog-card class="_changelog-card" />
        <trial-box
          v-if="showTrialBox"
          class="_trial-box"
          @hide="disableTrialBox"
        />
        <div
          v-if="onboardingProgress !== 100"
          :class="{ '_disabled-when-expired': isExpired }"
          :style="{ width: '100%' }"
        >
          <nav-item
            :title="$t('mainNavigation.onboarding')"
            icon="rocket"
            hover-color="beige"
            force-show-actions
            @click="showOnboarding"
          >
            <template #actions>
              <progress-tag
                :progress="onboardingProgress"
                :style="{ border: `1px solid ${$colors.blue.darken2}` }"
              />
            </template>
          </nav-item>
        </div>

        <div class="_bottom-items">
          <m-btn
            id="help-and-support"
            class="_help-fab"
            icon="question-circle"
            fab
            hide-border
            light
            @click="showResourceCenter"
          />
          <m-btn
            v-if="canInviteUsers"
            :class="['_action-btn', '-right', { '_disabled-when-expired': isExpired }]"
            icon="user-group-add"
            hide-border
            light
            :disabled="isExpired"
            @click="showInvitePeopleDialog"
          >
            {{ $t('mainNavigation.invite') }}
          </m-btn>
        </div>
      </div>
    </div>
  </m-sider>
</template>

<script>
import AccountMenu from '@/components/AccountMenu.vue';
import AiChatNavSection from '@/components/ai-chat/AiChatNavSection.vue';
import ChangelogCard from '@/release-notes/ChangelogCard.vue';
import CollapsableNavItems from '@/components/navigation/CollapsableNavItems.vue';
import FavoritesNavItems from '@/components/navigation/FavoritesNavItems.vue';
import Inbox from '@/components/navigation/Inbox.vue';
import MSider from '@/components/layout/MSider.vue';
import MyWorkspace from '@/components/navigation/MyWorkspace.vue';
import NavItem from '@/components/navigation/NavItem.vue';
import NavItems from '@/components/navigation/NavItems.vue';
import NavSubHeading from '@/components/navigation/NavSubHeading.vue';
import ProgressTag from '@/components/ProgressTag.vue';
import SearchBox from '@/components/navigation/SearchBox.vue';
import SidebarIconTab from '@/components/navigation/SidebarIconTab.vue';
import Trash from '@/components/navigation/Trash.vue';
import TrialBox from '@/components/navigation/TrialBox.vue';
import useAIChat from '@/composables/ai-chat/ai-chat';
import useAccess from '@/composables/access/access';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useExpand from '@/composables/expand';
import useFavorites from '@/composables/favorite/favorite';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useLocalStorage from '@/composables/local-storage/local-storage';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useMSTeams from '@/composables/msteams';
import useOnboardingMarker from '@/composables/onboarding/onboarding-marker';
import useOnboardingSteps from '@/composables/onboarding/onboarding-steps';
import useViewNavigator from '@/composables/saved-views/navigator';
import { DateTime } from 'luxon';
import { EventBus } from '@/lib/event-bus';
import { SIDEBAR_SERVICE } from '@/lib/constants';
import { accessGroupFlag, moduleFlag, productPlan, routeName } from 'shared/constants.json';
import { computed, inject } from 'vue';
import { mapState } from 'vuex';

export default {
  name: 'MainNavigation',
  props: {
    bordered: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      required: true,
    },
  },
  emits: ['toggle-sidebar'],
  setup() {
    const {
      accountHasFeature,
      userHasRight,
    } = useAccess();
    const { loggedInUserAccount, isExpired } = useLoggedInUserAccount();
    const { goalSettings } = useGoalSettings();
    const msTeamsSvc = useMSTeams();
    const { userLang } = useLoggedInUser();
    const identifier = { getId: (o) => o.id };
    const keyMaker = {
      getKey() {
        return computed(() => `${loggedInUserAccount.value.uid}_main_navigation_expand`);
      },
    };
    const localStorageSvc = useLocalStorage(localStorage, keyMaker);
    const expandSvc = useExpand(identifier, localStorageSvc.data);

    const subHeadingKeyMaker = {
      getKey() {
        return computed(() => `${loggedInUserAccount.value.uid}_main_navigation_subheading_collapse`);
      },
    };
    const subHeadingLocalStorageSvc = useLocalStorage(
      localStorage,
      subHeadingKeyMaker,
      { items: { favorites: true, spaces: true, tools: true } },
    );
    const subHeadingExpandSvc = useExpand({ getId: (o) => o.id }, subHeadingLocalStorageSvc.data);

    const accountSettingsSvc = useAccountSettings();
    const accessSvc = useAccess();
    const onboardingMarker = useOnboardingMarker(loggedInUserAccount, accountSettingsSvc);
    const { progress } = useOnboardingSteps(
      onboardingMarker,
      {},
      accountSettingsSvc,
      accessSvc,
    );

    const { favorites } = useFavorites();

    const { linkToRoute } = useViewNavigator();
    const { canUseAIChat } = useAIChat();
    const { desktopSidebarHidden } = inject(SIDEBAR_SERVICE);
    return {
      canUseAIChat,
      desktopSidebarHidden,
      userLang,
      linkToRoute,
      accountHasFeature,
      userHasRight,
      account: loggedInUserAccount,
      isMsTeams: msTeamsSvc.isMsTeams,
      onboardingProgress: progress,
      expandSvc,
      canInviteUsers: userHasRight([accessGroupFlag.userWriteAccess]),
      isExpired,
      favorites,
      goalSettings,
      subHeadingExpandSvc,
    };
  },
  components: {
    ChangelogCard,
    SearchBox,
    SidebarIconTab,
    Inbox,
    AiChatNavSection,
    Trash,
    CollapsableNavItems,
    MyWorkspace,
    TrialBox,
    NavItem,
    NavSubHeading,
    MSider,
    NavItems,
    AccountMenu,
    FavoritesNavItems,
    ProgressTag,
  },
  data() {
    return {
      drawer: true,
      showAccountOnboardingGuide: false,
      hover: false,
      trialBoxDisabled: false,
      routeName,
      showTopBorder: false,
      showBottomBorder: false,
      sidebarTab: 'workspace',
    };
  },
  computed: {
    ...mapState({ breakpoint: (state) => state.breakpoint }),
    onAiChatRoute() {
      return this.$route.name === routeName.aiChat;
    },
    showsAiChatTab() {
      return this.canUseAIChat && this.sidebarTab === 'ai-chat';
    },
    showTrialBox() {
      if (this.breakpoint.smAndDown && this.isMsTeams) {
        return false;
      }

      return this.account.accountSettings.planId === productPlan.trial && !this.trialBoxDisabled;
    },
    favoritesExpanded() {
      return this.subHeadingExpandSvc.getExpand({ id: 'favorites' }).value;
    },
    spacesExpanded() {
      return this.subHeadingExpandSvc.getExpand({ id: 'spaces' }).value;
    },
    toolsExpanded() {
      return this.subHeadingExpandSvc.getExpand({ id: 'tools' }).value;
    },
    goalsEnabled() {
      return this.accountHasFeature([moduleFlag.goals]);
    },
    topNavigation1() {
      const res = [];
      res.push({
        ...this.linkToRoute(this.$t('navigation.home'), routeName.home),
        id: 'home',
        icon: 'home',
      });
      return res;
    },
    tabNavigation() {
      return [
        { id: 'workspace', icon: 'moon', title: this.$t('mainNavigation.workspace') },
        { id: 'ai-chat', icon: 'astronaut', title: this.$t('aiChat.title') },
      ];
    },
    bottomNavigation() {
      return [
        {
          id: 'settings',
          icon: 'settings',
          title: this.$t('navigation.settings'),
          to: { name: routeName.profileSettings },
        },
      ];
    },
    tools() {
      const res = [];
      if (this.goalsEnabled) {
        const children = [
          this.linkToRoute(this.$t('navigation.explorer'), routeName.goalsExplorer),
          this.linkToRoute(this.$t('goalsExplorer.feed'), routeName.goalUpdateFeed),
        ];
        res.push({
          id: 'goals',
          icon: 'compass',
          title: this.goalSettings.featureNamePlural,
          to: children[0].to,
          active: children.some((link) => link.active),
          children,
        });
      }
      if (this.accountHasFeature([moduleFlag.updates])) {
        const children = [
          this.linkToRoute(this.$t('navigation.explorer'), routeName.updatesExplorer),
          this.linkToRoute(this.$t('updatesList.schedules'), routeName.updateSchedules),
          this.linkToRoute(this.$t('updatesList.templates'), routeName.updateTemplates),
        ];
        res.push({
          id: 'updates',
          icon: 'history',
          title: this.$t('navigation.updatesExplorer'),
          to: children[0].to,
          active: children.some((link) => link.active),
          children,
        });
        // Force expansion when showCheckinTour to access the desired elements
        if (this.$route.query.showCheckinTour !== undefined && JSON.parse(this.$route.query.showCheckinTour) === true) {
          this.expandSvc.expandItem({ id: 'updates' });
        }
      }
      if (this.accountHasFeature([moduleFlag.plannings])) {
        res.push({
          id: 'planning',
          icon: 'plan',
          title: this.$t('navigation.plannings'),
          to: { name: routeName.planningList },
          active: [routeName.planningList].includes(this.$route.name),
        });
      }

      const children = [
        this.linkToRoute(this.$t('dashboards.list'), routeName.dashboards),
        this.linkToRoute(this.$t('dashboards.reports'), routeName.scheduledReports),
      ];
      res.push({
        id: 'dashboard',
        icon: 'appstore',
        title: this.$t('navigation.dashboards'),
        to: children[0].to,
        active: children.some((link) => link.active),
        children,
      });

      return res;
    },
  },
  methods: {
    showResourceCenter() {
      EventBus.$emit('show-resource-center');
    },
    showOnboarding() {
      EventBus.$emit('show-onboarding');
    },
    showInvitePeopleDialog() {
      EventBus.$emit('show-invite-users-dialog');
    },
    disableTrialBox() {
      localStorage.setItem('trialBoxDisabled', DateTime.local().toISO());
      this.trialBoxDisabled = true;
    },
    toggleSection(section) {
      this.subHeadingExpandSvc.toggleExpand({ id: section });
    },
    handleMiddleScroll() {
      if (this.$refs.middleRef !== undefined && this.$refs.middleRef !== null) {
        const el = this.$refs.middleRef;
        this.showTopBorder = el.scrollTop > 0;
        this.showBottomBorder = (el.scrollTop + el.clientHeight) < el.scrollHeight;
      }
    },
  },
  watch: {
    onAiChatRoute: {
      handler(onAiChat) {
        this.sidebarTab = onAiChat ? 'ai-chat' : 'workspace';
      },
      immediate: true,
    },
  },
  created() {
    if (typeof this.$route.query.showOnboarding !== 'undefined') {
      this.showAccountOnboardingGuide = true;
    }

    const trialBoxDisabled = localStorage.getItem('trialBoxDisabled');
    if (trialBoxDisabled === null) {
      return;
    }

    const d = DateTime.fromISO(trialBoxDisabled);
    if (d.isValid && d.diff(DateTime.local()).as('hours') < -24) {
      return;
    }

    this.trialBoxDisabled = true;
  },
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .main-navigation {
    position: relative;
    z-index: 1;
    background-color: $side-nav-background-color;

    ._content {
      position: relative;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;

      ._top {
        position: sticky;
        top: 0;
        z-index: 1;
        display: flex;
        flex-direction: column;
        overflow: visible;
        padding: .4rem .6rem;
        background-color: $side-nav-background-color;
        transition: border-color .15s ease-in-out;
        border-bottom: .1rem solid transparent;
      }

      ._header {
        display: flex;
        align-items: center;

        ._account-menu {
          min-width: 0;

          &.-hug {
            width: auto;
          }
        }

        ._header-actions {
          display: flex;
          gap: .2rem;
          align-items: center;
          margin-left: auto;
        }
      }

      ._tabs {
        display: flex;
        gap: .4rem;
        padding: .8rem .4rem .4rem;
      }

      ._top.-line {
        border-bottom-color: $border-color;
      }

      ._middle {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: .4rem 0.6rem 0;
        min-height: 0;

        ._secondary-items {
          padding: 2.4rem 0;
        }
      }

      ._bottom {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: auto;
        position: sticky;
        bottom: 0;
        background-color: $side-nav-background-color;
        border-top: .1rem solid transparent;
        transition: border-top-color .15s ease-in-out;

        ._changelog-card,
        ._trial-box {
          margin-bottom: .6rem;
        }

        ._bottom-items {
          display: flex;
          align-items: center;
          width: 100%;
          padding: .8rem .6rem 1rem;
          gap: .8rem;

          ._action-btn {
            font-weight: $font-weight-medium;
            margin-left: auto;
          }
        }
      }

      ._bottom.-line {
        border-top-color: $border-color;
      }
    }

    ._action {
      visibility: hidden;
    }

    &:hover {
      ._action {
        visibility: visible;
      }
    }

    &.-mobile {
      ._action {
        visibility: visible;
      }
    }

    ._disabled-when-expired {
      opacity: .5;

      :deep(*) {
        pointer-events: none;
      }
    }
  }

  ._group.-expanded {
    margin-bottom: 1.2rem;
  }

</style>
